import { ArrowDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSubscription, type Plan } from '@/hooks/useSubscription';

interface DowngradeBannerProps {
  scheduledPlan: Plan;
  scheduledDate: string;
}

/**
 * Banner displayed when a subscription has a scheduled downgrade.
 * Shows the target plan and scheduled date with option to cancel the downgrade.
 */
export function DowngradeBanner({ scheduledPlan, scheduledDate }: DowngradeBannerProps) {
  const { cancelDowngrade, isCancelDowngradeLoading } = useSubscription();

  // Format the scheduled date for display
  const formattedDate = new Date(scheduledDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const handleCancelDowngrade = async () => {
    try {
      await cancelDowngrade();
    } catch {
      // Error handling is done in the hook via toast
    }
  };

  return (
    <div className="rounded-lg border border-sapling-secondary-100 bg-sapling-secondary-100/10 px-4 py-3">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <ArrowDown className="h-5 w-5 shrink-0 text-sapling-secondary-100" />
          <div>
            <p className="font-medium text-sapling-secondary-200">
              Downgrade scheduled to {scheduledPlan.plan_name}
            </p>
            <p className="text-sm text-muted-foreground">
              Your plan will change to {scheduledPlan.plan_name} (${scheduledPlan.price}/{scheduledPlan.billing_period === 'yearly' ? 'year' : 'month'}) on {formattedDate}.
              You'll keep your current plan until then.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCancelDowngrade}
          loading={isCancelDowngradeLoading}
          className="shrink-0"
        >
          <X className="mr-1 h-4 w-4" />
          Cancel Downgrade
        </Button>
      </div>
    </div>
  );
}
